import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

/** Top-level UI mode for users that have access to both apps. */
export type AppMode = "patient" | "setup";

/** localStorage key for persisting the user's mode choice across reloads. */
export const APP_MODE_STORAGE_KEY = "circadia_app_mode";

const readPersistedAppMode = (): AppMode | undefined => {
  try {
    if (typeof window === "undefined") return undefined;
    const v = window.localStorage.getItem(APP_MODE_STORAGE_KEY);
    return v === "patient" || v === "setup" ? v : undefined;
  } catch {
    return undefined;
  }
};

const writePersistedAppMode = (mode: AppMode | undefined): void => {
  try {
    if (typeof window === "undefined") return;
    if (mode === undefined) {
      window.localStorage.removeItem(APP_MODE_STORAGE_KEY);
    } else {
      window.localStorage.setItem(APP_MODE_STORAGE_KEY, mode);
    }
  } catch {
    /* best-effort */
  }
};

export interface IAuthStateSlice {
  isAuthorized?: boolean;
  isPinAuthorized?: boolean;
  authState?: string;
  error?: string;
  user?: User;
  loginState?: string;
  challenge?: string;
  userPreference?: UserPreference;
  pinTries: number;
  pinLockTime: number | null;
  hasNoAccess: boolean | null;
  smsData?: {
    phoneNumber: string;
    isVerified: boolean;
  };
  /** Which app shell the user is currently in. Persisted to localStorage. */
  appMode?: AppMode;
  /**
   * Set while transitioning between app shells (setup ⇄ patient). Drives the
   * full-screen overlay in `AppRouter` so the user doesn't see the old shell
   * tear down and the new one paint underneath. Not persisted — purely UI.
   */
  modeSwitching?: {
    /** Mode the user is heading toward; lets the overlay tweak its copy. */
    target: AppMode;
  };
}

const name = "Auth";

export const initialState: IAuthStateSlice = {
  isAuthorized: false,
  isPinAuthorized: false,
  authState: "pending",
  error: "",
  user: undefined,
  loginState: "login",
  userPreference: undefined,
  pinTries: 0,
  pinLockTime: null,
  hasNoAccess: null,
  smsData: undefined,
  appMode: readPersistedAppMode(),
  modeSwitching: undefined,
};

export const authSlice = createSlice({
  name,
  initialState,
  reducers: {
    setAuthState: (state, action: PayloadAction<Partial<IAuthStateSlice>>) => ({
      ...state,
      ...action.payload,
    }),
    setPinTries: (state, action: PayloadAction<number>) => {
      state.pinTries = action.payload;
    },
    setPinLockTime: (state, action: PayloadAction<number | null>) => {
      state.pinLockTime = action.payload;
    },
    setUser: (state, action: PayloadAction<Partial<User>>) => {
      state.user = { ...state.user, ...action.payload };
    },
    setAppMode: (state, action: PayloadAction<AppMode | undefined>) => {
      state.appMode = action.payload;
      writePersistedAppMode(action.payload);
    },
    setModeSwitching: (
      state,
      action: PayloadAction<{ target: AppMode } | undefined>
    ) => {
      state.modeSwitching = action.payload;
    },
  },
});

export const {
  setAuthState,
  setPinTries,
  setPinLockTime,
  setUser,
  setAppMode,
  setModeSwitching,
} = authSlice.actions;

export default authSlice.reducer;
